// API service for communicating with FastAPI backend
// Default to backend running on port 8000 (backend/main.py uses 8000)
// Use current hostname so the frontend served on LAN (e.g., 192.168.x.x:3000)
// talks to the backend on the same host instead of 127.0.0.1 from the client.
const isDev = process.env.NODE_ENV === 'development';
const canProxy = typeof window !== 'undefined' && (window.location.port === '3000' || window.location.port === '3001');
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  ((isDev && canProxy)
    ? ''
    : (typeof window !== 'undefined'
        ? 'https://api-report.pxi.maxzrange.com'
        : 'https://api-report.pxi.maxzrange.com'));

const notifyStorageUsageChanged = () => {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('storage-usage-refresh'));
    }
  } catch {}
};

const shouldNotifyStorageUsageChanged = (endpoint, config = {}) => {
  const method = String(config.method || 'GET').toUpperCase();
  if (method === 'GET') return false;
  if (endpoint.startsWith('/api/storage-usage')) return false;
  if (endpoint.startsWith('/auth/')) return false;
  return true;
};

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method for making HTTP requests
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...options.headers,
      },
      cache: 'no-store',
      ...options,
    };

    // Add authentication token if available
    const token = localStorage.getItem('authToken');
    const isAuthEndpoint = endpoint.startsWith('/auth/') || endpoint.startsWith('/health') || endpoint.startsWith('/api/health');
    if (token && !isAuthEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const rawText = await response.text().catch(() => '');
        let errorData = {};
        try {
          errorData = rawText ? JSON.parse(rawText) : {};
        } catch {
          errorData = {};
        }
        const detail = (errorData && typeof errorData.detail === 'string') ? errorData.detail : '';
        const fallback = rawText ? rawText.slice(0, 500) : '';
        const err = new Error(detail || fallback || `HTTP error! status: ${response.status}`);
        err.status = response.status;
        err.endpoint = endpoint;
        throw err;
      }

      const result = await response.json();
      if (shouldNotifyStorageUsageChanged(endpoint, config)) {
        notifyStorageUsageChanged();
      }
      return result;
    } catch (error) {
      if (!options.quiet) {
        console.error(`API request failed: ${endpoint}`, error);
      }
      throw error;
    }
  }

  // Building plans APIs (Section 2)
  async getBuildingPlans(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/plans`);
  }

  async createBuildingPlan(buildingId, planData) {
    return this.makeRequest(`/api/buildings/${buildingId}/plans`, {
      method: 'POST',
      body: JSON.stringify(planData),
    });
  }

  async updateBuildingPlan(buildingId, planId, planData) {
    return this.makeRequest(`/api/buildings/${buildingId}/plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(planData),
    });
  }

  async deleteBuildingPlan(buildingId, planId) {
    return this.makeRequest(`/api/buildings/${buildingId}/plans/${planId}`, {
      method: 'DELETE',
    });
  }

  async reorderBuildingPlans(buildingId, planOrders) {
    return this.makeRequest(`/api/buildings/${buildingId}/plans/reorder`, {
      method: 'PUT',
      body: JSON.stringify(planOrders),
    });
  }

  async copyBuildingPlansToProject(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/plans/copy-to-project`, {
      method: 'POST',
    });
  }

  async uploadBuildingPlan(buildingId, file, name, description = '') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description || '');
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/plans/upload`, {
      method: 'POST',
      body: formData,
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const result = await response.json();
    notifyStorageUsageChanged();
    return result;
  }

  async createBuildingPlanFromUrl(buildingId, name, fileUrl, description = '') {
    return this.makeRequest(`/api/buildings/${buildingId}/plans/from-url`, {
      method: 'POST',
      body: JSON.stringify({
        name,
        file_url: fileUrl,
        description,
      }),
    });
  }

  // Annex APIs
  async getAnnexes(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/annexes`);
  }

  async createAnnex(buildingId, data) {
    return this.makeRequest(`/api/buildings/${buildingId}/annexes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAnnex(buildingId, annexId, data) {
    return this.makeRequest(`/api/buildings/${buildingId}/annexes/${annexId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAnnex(buildingId, annexId) {
    return this.makeRequest(`/api/buildings/${buildingId}/annexes/${annexId}`, {
      method: 'DELETE',
    });
  }

  async uploadAnnexFile(buildingId, file, title, content = '') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('content', content || '');
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/annexes/upload`, {
      method: 'POST',
      body: formData,
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const result = await response.json();
    notifyStorageUsageChanged();
    return result;
  }

  async uploadAnnexFolder(buildingId, files, title = null, content = '') {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      const fileToUpload = file.webkitRelativePath
        ? new File([file], file.webkitRelativePath, { type: file.type })
        : file;
      formData.append('files', fileToUpload);
    });
    if (title) formData.append('title', title);
    formData.append('content', content || '');
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/annexes/upload-folder`, {
      method: 'POST',
      body: formData,
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const result = await response.json();
    notifyStorageUsageChanged();
    return result;
  }

  // Health check
  async healthCheck() {
    return this.makeRequest('/health');
  }

  async getStorageUsage(path = null) {
    const query = path ? `?path=${encodeURIComponent(path)}` : '';
    return this.makeRequest(`/api/storage-usage${query}`);
  }

  // Authentication methods
  async login(email, password) {
    return this.makeRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async registerUser({ email, name, password, group_id = null }) {
    return this.makeRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, name, password, group_id }),
    });
  }

  async logout() {
    try {
      await this.makeRequest('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API call success
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }

  async verifyToken() {
    try {
      return await this.makeRequest('/auth/verify', { quiet: true });
    } catch (error) {
      // If token verification fails, clear local storage and return null silently
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      return null;
    }
  }

  async getNotifications(limit = 50) {
    return this.makeRequest(`/api/notifications?limit=${encodeURIComponent(limit)}`);
  }

  async saveNotification(notification) {
    return this.makeRequest('/api/notifications', {
      method: 'POST',
      body: JSON.stringify(notification || {}),
    });
  }

  async markNotificationRead(notificationId) {
    return this.makeRequest(`/api/notifications/${encodeURIComponent(notificationId)}/read`, {
      method: 'PUT',
    });
  }

  async markAllNotificationsRead() {
    return this.makeRequest('/api/notifications/read-all', {
      method: 'PUT',
    });
  }

  async clearNotifications() {
    return this.makeRequest('/api/notifications', {
      method: 'DELETE',
    });
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('authToken');
  }

  // Get current user from local storage
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Upload images to existing elevation folder
  async uploadImagesToElevation(files, elevationName, projectId, buildingId = null, imageType = 'drone') {
    try {
      const formData = new FormData();
      
      // Add files to FormData
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });
      
      // Add required parameters
      formData.append('elevation_name', elevationName);
      formData.append('project_id', projectId);
      
      // Add optional building_id if provided
      if (buildingId) {
        formData.append('building_id', buildingId);
      }
      
      // Add image_type if provided
      if (imageType) {
        formData.append('image_type', imageType);
      }
      
      const response = await fetch(`${this.baseURL}/api/upload-images-to-elevation`, {
        method: 'POST',
        body: formData,
        cache: 'no-store',
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Upload to elevation successful:', result);
        notifyStorageUsageChanged();
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error uploading images to elevation:', error);
      throw error;
    }
  }

  async uploadImageToElevation(file, elevationName, projectId, buildingId = null, imageType = 'drone') {
    return this.uploadImagesToElevation([file], elevationName, projectId, buildingId, imageType);
  }

  // Upload folder with images
  async uploadFolder(files, elevationName = null, projectId = null, buildingId = null, imageType = 'drone') {
    try {
      const formData = new FormData();
      
      // Add files to FormData with their relative paths preserved
      const orderedFiles = Array.from(files).sort((a, b) => {
        const aKey = String(a?.webkitRelativePath || a?.name || '');
        const bKey = String(b?.webkitRelativePath || b?.name || '');
        return aKey.localeCompare(bKey, undefined, { numeric: true, sensitivity: 'base' });
      });

      orderedFiles.forEach((file) => {
        // Create a new File object with the webkitRelativePath if available
        const fileToUpload = file.webkitRelativePath ? 
          new File([file], file.webkitRelativePath, { type: file.type }) : 
          file;
        formData.append('files', fileToUpload);
      });
      
      // Add elevation name if provided
      if (elevationName) {
        formData.append('elevation_name', elevationName);
      }
      
      // Add project_id if provided
      if (projectId) {
        formData.append('project_id', projectId);
      }
      
      // Add building_id if provided
      if (buildingId) {
        formData.append('building_id', buildingId);
      }

      // Add image_type if provided
      if (imageType) {
        formData.append('image_type', imageType);
      }
      
      const response = await fetch(`${this.baseURL}/api/upload-folder`, {
        method: 'POST',
        body: formData,
        cache: 'no-store',
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Upload successful:', result);
        notifyStorageUsageChanged();
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error uploading folder:', error);
      throw error;
    }
  }

  async uploadFolderFile(file, elevationName = null, projectId = null, buildingId = null, imageType = 'drone') {
    return this.uploadFolder([file], elevationName, projectId, buildingId, imageType);
  }

  // Get folder structure from backend
  // Get folder structure
  async getFolderStructure(projectId = null) {
    const url = projectId ? `/api/folder-structure?project_id=${projectId}` : '/api/folder-structure';
    return this.makeRequest(url);
  }

  // Get project images
  async getProjectImages(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/images`);
  }

  // Get building images
  async getBuildingImages(buildingId, imageType = null) {
    const query = imageType ? `?image_type=${encodeURIComponent(imageType)}` : '';
    return this.makeRequest(`/api/buildings/${buildingId}/images${query}`);
  }

  async getElevationOrder(buildingId, imageType = 'drone') {
    const query = imageType ? `?image_type=${encodeURIComponent(imageType)}` : '';
    return this.makeRequest(`/api/buildings/${buildingId}/elevation-order${query}`);
  }

  async saveElevationOrder(buildingId, order, imageType = 'drone') {
    return this.makeRequest(`/api/buildings/${buildingId}/elevation-order`, {
      method: 'PUT',
      body: JSON.stringify({ order: order || [], image_type: imageType || 'drone' }),
    });
  }

  async renameElevation(oldName, newName, projectId = null, buildingId = null, imageType = 'drone', imageIds = []) {
    return this.makeRequest('/api/elevations/rename', {
      method: 'PUT',
      body: JSON.stringify({
        old_name: oldName,
        new_name: newName,
        project_id: projectId,
        building_id: buildingId,
        image_type: imageType || 'drone',
        image_ids: Array.isArray(imageIds) ? imageIds : [],
      }),
    });
  }
  
  async getElevationCover(buildingId, imageType = 'drone') {
    const query = imageType ? `?image_type=${encodeURIComponent(imageType)}` : '';
    return this.makeRequest(`/api/buildings/${buildingId}/elevation-cover${query}`);
  }
  
  async setElevationCover(buildingId, elevationName, imageId, imageType = 'drone') {
    return this.makeRequest(`/api/buildings/${buildingId}/elevation-cover`, {
      method: 'PUT',
      body: JSON.stringify({ elevation_name: elevationName, image_id: imageId, image_type: imageType || 'drone' }),
    });
  }
  
  async listAnnexMaster() {
    return this.makeRequest(`/api/annex-master`, { method: 'GET' });
  }
  
  async addAnnexMaster(title) {
    return this.makeRequest(`/api/annex-master`, {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  }
  
  async updateAnnexMaster(id, title) {
    return this.makeRequest(`/api/annex-master/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ title }),
    });
  }

  async uploadAnnexMasterFile(id, files) {
    const formData = new FormData();
    const arr = Array.isArray(files) ? files : [files];
    arr.filter(Boolean).forEach((f) => {
      formData.append('files', f);
    });
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseURL}/api/annex-master/${id}/upload`, {
      method: 'POST',
      body: formData,
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const result = await response.json();
    notifyStorageUsageChanged();
    return result;
  }
  
  async deleteAnnexMaster(id) {
    return this.makeRequest(`/api/annex-master/${id}`, { method: 'DELETE' });
  }
  
  async getUASReport(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/uas`, { method: 'GET' });
  }
  
  async saveUASReport(buildingId, data) {
    return this.makeRequest(`/api/buildings/${buildingId}/uas`, {
      method: 'PUT',
      body: JSON.stringify(data || {}),
    });
  }
  
  async listKeyFindings(buildingId, imageType = 'drone') {
    return this.makeRequest(`/api/buildings/${buildingId}/key-findings?image_type=${encodeURIComponent(imageType || 'drone')}`, { method: 'GET' });
  }
  
  async addKeyFinding(buildingId, imageId, imageType = 'drone') {
    return this.makeRequest(`/api/buildings/${buildingId}/key-findings`, {
      method: 'POST',
      body: JSON.stringify({ image_id: imageId, image_type: imageType || 'drone' }),
    });
  }
  
  async removeKeyFinding(buildingId, imageId, imageType = 'drone') {
    return this.makeRequest(`/api/buildings/${buildingId}/key-findings/${encodeURIComponent(imageId)}?image_type=${encodeURIComponent(imageType || 'drone')}`, {
      method: 'DELETE',
    });
  }

  // Clear folder structure (for demo purposes)
  async clearFolderStructure() {
    try {
      // Since we don't have a clear endpoint in the backend, we'll just return success
      console.log('Folder structure cleared (frontend only)');
      return { success: true, message: 'Folder structure cleared' };
    } catch (error) {
      console.error('Error clearing folder structure:', error);
      throw error;
    }
  }

  // Add defect annotation
  async addDefectAnnotation(annotationData) {
    try {
      // Extract image_id from the annotation data
      const imageId = annotationData.image_id;
      
      // Create the defect data structure expected by the backend
      const defectData = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9), // Generate unique ID
        x: annotationData.coordinates?.[0]?.x || 0,
        y: annotationData.coordinates?.[0]?.y || 0,
        width: annotationData.coordinates?.[0]?.width || 0,
        height: annotationData.coordinates?.[0]?.height || 0,
        defect_type: annotationData.defect_type || 'Unknown',
        severity: 'Medium', // Default severity
        recommendation: annotationData.recommendation || '',
        project_id: annotationData.project_id || null, // Include project_id
        created_at: new Date().toISOString()
      };

      const response = await fetch(`${this.baseURL}/api/images/${imageId}/defects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(defectData),
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Defect annotation added:', result);
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to add defect annotation: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error adding defect annotation:', error);
      throw error;
    }
  }

  // Get defects for an image
  async getImageDefects(imageId) {
    try {
      const response = await fetch(`${this.baseURL}/api/images/${imageId}/defects`, { cache: 'no-store' });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Image defects retrieved:', result);
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to get image defects: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error getting image defects:', error);
      throw error;
    }
  }

  // Update defect annotation
  async updateDefectAnnotation(imageId, defectId, defectData) {
    try {
      const response = await fetch(`${this.baseURL}/api/images/${imageId}/defects/${defectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(defectData),
        cache: 'no-store',
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Defect annotation updated:', result);
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to update defect annotation: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating defect annotation:', error);
      throw error;
    }
  }

  // Delete defect annotation
  async deleteDefectAnnotation(imageId, defectId) {
    try {
      const response = await fetch(`${this.baseURL}/api/images/${imageId}/defects/${defectId}`, {
        method: 'DELETE',
        cache: 'no-store',
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Defect annotation deleted:', result);
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`Failed to delete defect annotation: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error deleting defect annotation:', error);
      throw error;
    }
  }

  async getSitePlanAnnotations(buildingId) {
    try {
      const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/siteplan-annotations`, {
        cache: 'no-store',
      });
      if (response.ok) {
        const result = await response.json();
        return result;
      }
      const errorText = await response.text();
      throw new Error(`Failed to get site plan annotations: ${response.status} ${response.statusText} - ${errorText}`);
    } catch (error) {
      console.error('Error getting site plan annotations:', error);
      throw error;
    }
  }

  async saveSitePlanAnnotations(buildingId, payload) {
    try {
      const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/siteplan-annotations`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        cache: 'no-store',
      });
      if (response.ok) {
        const result = await response.json();
        return result;
      }
      const errorText = await response.text();
      throw new Error(`Failed to save site plan annotations: ${response.status} ${response.statusText} - ${errorText}`);
    } catch (error) {
      console.error('Error saving site plan annotations:', error);
      throw error;
    }
  }

  // BuildingInfo methods
  async getBuildingInfo(buildingId) {
    try {
      const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/info`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {}),
        },
        cache: 'no-store',
      });
      if (response.status === 404) {
        return null; // No info yet
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get building info: ${response.status} ${response.statusText} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting building info:', error);
      throw error;
    }
  }

  async createBuildingInfo(buildingId, infoData) {
    return this.makeRequest(`/api/buildings/${buildingId}/info`, {
      method: 'POST',
      body: JSON.stringify(infoData),
    });
  }

  async updateBuildingInfo(buildingId, infoData) {
    return this.makeRequest(`/api/buildings/${buildingId}/info`, {
      method: 'PUT',
      body: JSON.stringify(infoData),
    });
  }

  async deleteBuildingInfo(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/info`, {
      method: 'DELETE',
    });
  }

  async getVisualInspectionDiary(buildingId) {
    try {
      const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/inspection/visual`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {}),
        },
        cache: 'no-store',
      });
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get visual inspection diary: ${response.status} ${response.statusText} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting visual inspection diary:', error);
      throw error;
    }
  }

  async saveVisualInspectionDiary(buildingId, data) {
    return this.makeRequest(`/api/buildings/${buildingId}/inspection/visual`, {
      method: 'PUT',
      body: JSON.stringify(data || {}),
    });
  }

  async copyBuildingInfoToProject(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/info/copy-to-project`, {
      method: 'POST',
    });
  }

  // Close-range inspection (Section 3.1) methods
  async getCloseRangeInspection(buildingId) {
    try {
      const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/inspection/close-range`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('authToken') ? { Authorization: `Bearer ${localStorage.getItem('authToken')}` } : {}),
        },
        cache: 'no-store',
      });
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to get close-range inspection: ${response.status} ${response.statusText} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting close-range inspection:', error);
      throw error;
    }
  }

  async createCloseRangeInspection(buildingId, data) {
    return this.makeRequest(`/api/buildings/${buildingId}/inspection/close-range`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCloseRangeInspection(buildingId, data) {
    return this.makeRequest(`/api/buildings/${buildingId}/inspection/close-range`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCloseRangeInspection(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/inspection/close-range`, {
      method: 'DELETE',
    });
  }

  // Delete an image
  async deleteImage(imageId) {
    try {
      const response = await fetch(`${this.baseURL}/api/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      notifyStorageUsageChanged();
      return result;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  }

  async deleteElevation(elevationName, projectId = null, buildingId = null, imageType = 'drone') {
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    if (buildingId) params.set('building_id', buildingId);
    if (imageType) params.set('image_type', imageType);
    const query = params.toString();
    const encodedName = encodeURIComponent(elevationName);
    const result = await this.makeRequest(`/api/elevations/${encodedName}${query ? `?${query}` : ''}`, {
      method: 'DELETE',
    });
    return result;
  }

  // Defect types master list
  async getDefectTypes() {
    return this.makeRequest('/api/defect-types', { method: 'GET' });
  }

  async saveDefectTypes(defectTypes) {
    return this.makeRequest('/api/defect-types', {
      method: 'PUT',
      body: JSON.stringify({ items: Array.isArray(defectTypes) ? defectTypes : [] }),
    });
  }

  // Get image URL
  getImageUrl(imageId) {
    const url = `${this.baseURL}/api/images/${imageId}/file`;
    return url;
  }

  getImageThumbUrl(imageId, width = 400, format = 'webp') {
    const w = Number.isFinite(width) ? Math.max(80, Math.min(2000, Math.floor(width))) : 400;
    const fmt = String(format || 'webp');
    return `${this.baseURL}/api/images/${imageId}/thumb?w=${encodeURIComponent(w)}&fmt=${encodeURIComponent(fmt)}`;
  }

  // Project API methods
  // Get all projects
  async getProjects() {
    return this.makeRequest('/api/projects');
  }

  // Get projects with stats
  async getProjectsWithStats() {
    return this.makeRequest('/api/projects/stats');
  }

  // Get a specific project by ID
  async getProject(projectId) {
    return this.makeRequest(`/api/projects/${projectId}`);
  }

  async getProjectArchive(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/archive`);
  }

  async createProjectArchive(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/archive`, {
      method: 'POST',
    });
  }

  async restoreProjectArchive(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/archive`, {
      method: 'DELETE',
    });
  }

  async getBuildingArchive(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/archive`);
  }

  async createBuildingArchive(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/archive`, {
      method: 'POST',
    });
  }

  async restoreBuildingArchive(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/archive`, {
      method: 'DELETE',
    });
  }

  // Create a new project
  async createProject(projectData) {
    return this.makeRequest('/api/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  // Update an existing project
  async updateProject(projectId, projectData) {
    return this.makeRequest(`/api/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  }

  // Delete a project
  async deleteProject(projectId) {
    return this.makeRequest(`/api/projects/${projectId}`, {
      method: 'DELETE',
    });
  }

  // Reorder images
  // Building management methods
  async getProjectBuildings(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/buildings`);
  }

  async getBuilding(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}`);
  }

  async createBuilding(buildingData) {
    return this.makeRequest('/api/buildings', {
      method: 'POST',
      body: JSON.stringify(buildingData),
    });
  }

  async uploadBuildingCover(buildingId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('authToken');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${this.baseURL}/api/buildings/${buildingId}/cover/upload`, {
      method: 'POST',
      body: formData,
      headers,
      cache: 'no-store',
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const result = await response.json();
    notifyStorageUsageChanged();
    return result;
  }

  async updateBuilding(buildingId, buildingData) {
    return this.makeRequest(`/api/buildings/${buildingId}`, {
      method: 'PUT',
      body: JSON.stringify(buildingData),
    });
  }

  async deleteBuilding(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}`, {
      method: 'DELETE',
    });
  }

  // Reports: generate building report (PDF or DOCX)
  async generateBuildingReport(buildingId, format = 'PDF', payload = null, background = false) {
    const params = new URLSearchParams();
    if (format) params.set('out_format', format);
    if (background) params.set('background', '1');
    const query = params.toString() ? `?${params.toString()}` : '';
    const headers = payload
      ? {
          'Content-Type': 'application/json',
          ...(background ? { 'X-Report-Background': '1' } : {}),
        }
      : (background ? { 'X-Report-Background': '1' } : undefined);
    return this.makeRequest(`/api/buildings/${buildingId}/report${query}`, {
      method: 'POST',
      body: payload ? JSON.stringify(payload) : undefined,
      headers,
    });
  }

  // Reports: generate project report (PDF or DOCX) - Combined
  async generateProjectReport(projectId, format = 'PDF', payload = null, background = false) {
    const params = new URLSearchParams();
    if (format) params.set('out_format', format);
    if (background) params.set('background', '1');
    const query = params.toString() ? `?${params.toString()}` : '';
    const headers = payload
      ? {
          'Content-Type': 'application/json',
          ...(background ? { 'X-Report-Background': '1' } : {}),
        }
      : (background ? { 'X-Report-Background': '1' } : undefined);
    return this.makeRequest(`/api/projects/${projectId}/report${query}`, {
      method: 'POST',
      body: payload ? JSON.stringify(payload) : undefined,
      headers,
    });
  }

  async getBuildingReports(buildingId) {
    return this.makeRequest(`/api/buildings/${buildingId}/reports`);
  }

  async updateBuildingReportRetention(buildingId, filename, retained) {
    return this.makeRequest(`/api/buildings/${buildingId}/reports/${encodeURIComponent(filename)}/retention`, {
      method: 'PUT',
      body: JSON.stringify({ retained: Boolean(retained) }),
    });
  }

  async deleteBuildingReport(buildingId, filename) {
    const result = await this.makeRequest(`/api/buildings/${buildingId}/reports/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    });
    return result;
  }

  async getProjectReports(projectId) {
    return this.makeRequest(`/api/projects/${projectId}/reports`);
  }

  async updateProjectReportRetention(projectId, filename, retained) {
    return this.makeRequest(`/api/projects/${projectId}/reports/${encodeURIComponent(filename)}/retention`, {
      method: 'PUT',
      body: JSON.stringify({ retained: Boolean(retained) }),
    });
  }

  async deleteProjectReport(projectId, filename) {
    const result = await this.makeRequest(`/api/projects/${projectId}/reports/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
    });
    return result;
  }

  async getActiveReportJobs() {
    return this.makeRequest('/api/report-jobs/active');
  }

  async getReportJob(jobId) {
    return this.makeRequest(`/api/report-jobs/${jobId}`);
  }

  async reorderImages(imageOrders) {
    return this.makeRequest('/api/images/reorder', {
      method: 'PUT',
      body: JSON.stringify(imageOrders),
    });
  }

  // YOLO object detection
  async detectObjects(imageId, confidenceThreshold = 0.5, autoSave = false, blurCars = false, autoDetect = false) {
    try {
      const response = await fetch(`${this.baseURL}/api/images/${imageId}/detect-yolo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          confidence_threshold: confidenceThreshold,
          auto_save: autoSave,
          blur_cars: blurCars,
          auto_detect: autoDetect
        }),
        cache: 'no-store',
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('YOLO detection completed:', result);
        return result;
      } else {
        const errorText = await response.text();
        throw new Error(`YOLO detection failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error during YOLO detection:', error);
      throw error;
    }
  }

  async moveImageElevation(imageId, toElevation, orderPosition = null, imageType = null) {
    return this.makeRequest(`/api/images/${imageId}/move`, {
      method: 'PUT',
      body: JSON.stringify({
        to_elevation: toElevation,
        order_position: orderPosition,
        image_type: imageType,
      }),
    });
  }

  async updateImageLevel(imageId, floorName) {
    return this.makeRequest(`/api/images/${imageId}/level`, {
      method: 'PUT',
      body: JSON.stringify({ floor_name: floorName || '' }),
    });
  }

  async getGroups() {
    return this.makeRequest('/api/groups');
  }

  async createGroup(data) {
    return this.makeRequest('/api/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getUsers() {
    return this.makeRequest('/api/users');
  }

  async updateUser(userId, data) {
    return this.makeRequest(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(userId) {
    return this.makeRequest(`/api/users/${userId}`, {
      method: 'DELETE',
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();

if (typeof apiService.renameElevation !== 'function') {
  apiService.renameElevation = function renameElevation(oldName, newName, projectId = null, buildingId = null, imageType = 'drone', imageIds = []) {
    return this.makeRequest('/api/elevations/rename', {
      method: 'PUT',
      body: JSON.stringify({
        old_name: oldName,
        new_name: newName,
        project_id: projectId,
        building_id: buildingId,
        image_type: imageType || 'drone',
        image_ids: Array.isArray(imageIds) ? imageIds : [],
      }),
    });
  };
}

export default apiService;

// Export bound methods to preserve 'this' context
export const healthCheck = apiService.healthCheck.bind(apiService);
export const uploadFolder = apiService.uploadFolder.bind(apiService);
export const getFolderStructure = apiService.getFolderStructure.bind(apiService);
export const clearFolderStructure = apiService.clearFolderStructure.bind(apiService);
export const addDefectAnnotation = apiService.addDefectAnnotation.bind(apiService);
export const getImageDefects = apiService.getImageDefects.bind(apiService);
export const updateDefectAnnotation = apiService.updateDefectAnnotation.bind(apiService);
export const deleteDefectAnnotation = apiService.deleteDefectAnnotation.bind(apiService);
export const getSitePlanAnnotations = apiService.getSitePlanAnnotations.bind(apiService);
export const saveSitePlanAnnotations = apiService.saveSitePlanAnnotations.bind(apiService);
export const deleteImage = apiService.deleteImage.bind(apiService);
export const getImageUrl = apiService.getImageUrl.bind(apiService);
export const getProjects = apiService.getProjects.bind(apiService);
export const getProjectsWithStats = apiService.getProjectsWithStats.bind(apiService);
export const getProject = apiService.getProject.bind(apiService);
export const getProjectArchive = apiService.getProjectArchive.bind(apiService);
export const createProjectArchive = apiService.createProjectArchive.bind(apiService);
export const restoreProjectArchive = apiService.restoreProjectArchive.bind(apiService);
export const getBuildingArchive = apiService.getBuildingArchive.bind(apiService);
export const createBuildingArchive = apiService.createBuildingArchive.bind(apiService);
export const restoreBuildingArchive = apiService.restoreBuildingArchive.bind(apiService);
export const createProject = apiService.createProject.bind(apiService);
export const updateProject = apiService.updateProject.bind(apiService);
export const deleteProject = apiService.deleteProject.bind(apiService);
export const uploadImagesToElevation = apiService.uploadImagesToElevation.bind(apiService);
export const renameElevation = apiService.renameElevation.bind(apiService);
export const reorderImages = apiService.reorderImages.bind(apiService);
export const login = apiService.login.bind(apiService);
export const registerUser = apiService.registerUser.bind(apiService);
export const logout = apiService.logout.bind(apiService);
export const verifyToken = apiService.verifyToken.bind(apiService);
export const isAuthenticated = apiService.isAuthenticated.bind(apiService);
export const getProjectBuildings = apiService.getProjectBuildings.bind(apiService);
export const getBuilding = apiService.getBuilding.bind(apiService);
export const createBuilding = apiService.createBuilding.bind(apiService);
export const updateBuilding = apiService.updateBuilding.bind(apiService);
export const deleteBuilding = apiService.deleteBuilding.bind(apiService);
export const getCurrentUser = apiService.getCurrentUser.bind(apiService);
export const detectObjects = apiService.detectObjects.bind(apiService);
export const getGroups = apiService.getGroups.bind(apiService);
export const createGroup = apiService.createGroup.bind(apiService);
export const getUsers = apiService.getUsers.bind(apiService);
export const updateUser = apiService.updateUser.bind(apiService);
export const deleteUser = apiService.deleteUser.bind(apiService);
export const getBuildingInfo = apiService.getBuildingInfo.bind(apiService);
export const createBuildingInfo = apiService.createBuildingInfo.bind(apiService);
export const updateBuildingInfo = apiService.updateBuildingInfo.bind(apiService);
export const deleteBuildingInfo = apiService.deleteBuildingInfo.bind(apiService);
export const copyBuildingInfoToProject = apiService.copyBuildingInfoToProject.bind(apiService);
export const getCloseRangeInspection = apiService.getCloseRangeInspection.bind(apiService);
export const createCloseRangeInspection = apiService.createCloseRangeInspection.bind(apiService);
export const updateCloseRangeInspection = apiService.updateCloseRangeInspection.bind(apiService);
export const deleteCloseRangeInspection = apiService.deleteCloseRangeInspection.bind(apiService);
export const generateBuildingReport = apiService.generateBuildingReport.bind(apiService);
export const generateProjectReport = apiService.generateProjectReport.bind(apiService);
export const getBuildingReports = apiService.getBuildingReports.bind(apiService);
export const updateBuildingReportRetention = apiService.updateBuildingReportRetention.bind(apiService);
export const deleteBuildingReport = apiService.deleteBuildingReport.bind(apiService);
export const getProjectReports = apiService.getProjectReports.bind(apiService);
export const updateProjectReportRetention = apiService.updateProjectReportRetention.bind(apiService);
export const deleteProjectReport = apiService.deleteProjectReport.bind(apiService);
export const getBuildingPlans = apiService.getBuildingPlans.bind(apiService);
export const createBuildingPlan = apiService.createBuildingPlan.bind(apiService);
export const uploadBuildingPlan = apiService.uploadBuildingPlan.bind(apiService);
export const updateBuildingPlan = apiService.updateBuildingPlan.bind(apiService);
export const deleteBuildingPlan = apiService.deleteBuildingPlan.bind(apiService);
export const reorderBuildingPlans = apiService.reorderBuildingPlans.bind(apiService);
export const copyBuildingPlansToProject = apiService.copyBuildingPlansToProject.bind(apiService);
export const getAnnexes = apiService.getAnnexes.bind(apiService);
export const createAnnex = apiService.createAnnex.bind(apiService);
export const updateAnnex = apiService.updateAnnex.bind(apiService);
export const deleteAnnex = apiService.deleteAnnex.bind(apiService);
export const uploadAnnexFile = apiService.uploadAnnexFile.bind(apiService);
export const uploadAnnexFolder = apiService.uploadAnnexFolder.bind(apiService);
export const listAnnexMaster = apiService.listAnnexMaster.bind(apiService);
export const getBuildingImages = apiService.getBuildingImages.bind(apiService);
export const uploadBuildingCover = apiService.uploadBuildingCover.bind(apiService);
export const moveImageElevation = apiService.moveImageElevation.bind(apiService);
